import { lazy, Suspense } from "react";
import { AppBootstrapLoading } from "@ilabel/ui";
import { ErrorBoundary } from "react-error-boundary";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";

import { EditProtectionRouteBoundary } from "./edit-protection-navigation.js";
import { AppShell } from "./app-shell.js";
import { AppRouteError, AppRouteUnavailable } from "./app-route-error.js";
import { TeamMembersPageContext } from "./team-members-page-context.js";
import { IconSprite } from "./icons.js";
import {
  annotationDataManagementRoute,
  configManagementRoute,
  labelLibraryDetailRoute,
  loadOperationConsolePage,
  loginRoute,
  profileSettingsRoute,
  projectManagementRoute,
  datasetManagementRoute,
  teamMembersRoute,
  uiPreviewRoute,
  workbenchRoute,
} from "./route-loaders.js";
import { OperationConsoleStartupLoading } from "../features/operation-console/operation-console-loading.js";
import { useAppSession } from "./app-session-context.js";
import "./route-navigation.css";

function DefaultHomeRedirect() {
  const { session } = useAppSession();
  return <Navigate replace to={session?.defaultRoute || "/workbench"} />;
}

function AppLoadingState() {
  if (window.location.pathname === "/operation-console")
    return <OperationConsoleStartupLoading />;
  return (
    <AppBootstrapLoading
      ariaLabel="页面加载中"
      description="页面加载中…"
      indicator="bar"
      indicatorPlacement="after"
    />
  );
}

const LoginPage = lazy(loginRoute.load);
const OperationConsolePage = lazy(loadOperationConsolePage);
const UiPreviewPage = lazy(uiPreviewRoute.load);

const router = createBrowserRouter([{ element: <EditProtectionRouteBoundary />, HydrateFallback: AppLoadingState, errorElement: <AppRouteError />, children: [
  { path: "/login", element: <LoginPage /> },
  ...(import.meta.env.DEV
    ? [{ path: "/ui-preview", element: <UiPreviewPage /> }]
    : []),
  {
    path: "/operation-console",
    element: <OperationConsolePage />,
  },
  {
    path: "/",
    element: <AppShell />,
    children: [
      { index: true, element: <DefaultHomeRedirect /> },
      { path: "workbench", lazy: async () => ({ Component: (await workbenchRoute.load()).default }) },
      { path: "project-management", lazy: async () => ({ Component: (await projectManagementRoute.load()).default }) },
      { path: "dataset-management", lazy: async () => ({ Component: (await datasetManagementRoute.load()).default }) },
      {
        path: "annotation-data-management",
        lazy: async () => ({ Component: (await annotationDataManagementRoute.load()).default }),
      },
      { path: "config-management", lazy: async () => ({ Component: (await configManagementRoute.load()).default }) },
      { path: "label-library-detail", lazy: async () => ({ Component: (await labelLibraryDetailRoute.load()).default }) },
      { path: "team-members", lazy: async () => {
        const Page = (await teamMembersRoute.load()).default;
        return { Component: () => <TeamMembersPageContext><Page /></TeamMembersPageContext> };
      } },
      {
        path: "profile-settings",
        lazy: async () => ({ Component: (await profileSettingsRoute.load()).default }),
      },
      { path: "*", element: <AppRouteUnavailable /> },
    ],
  },
]}]);

export function App() {
  return (
    <>
      <IconSprite />
      <ErrorBoundary
        FallbackComponent={AppRouteError}
        onReset={() => window.location.reload()}
      >
        <Suspense fallback={<AppLoadingState />}>
          <RouterProvider router={router} />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}
